/**
 * @module NotificationBell
 * @package @geenius/notifications-react
 * @description Renders the bell trigger used to open the notification panel in
 * the Tailwind React variant. The component keeps unread counts accessible and
 * keyboard friendly without depending on an animation runtime.
 */

import { motion } from '@geenius/motion/react'
import type { JSX } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Props for the React notification bell trigger.
 */
export interface NotificationBellProps {
  unreadCount?: number
  onClick?: () => void
  isOpen?: boolean
  ariaControls?: string
  className?: string
}

/**
 * Renders the bell button used to toggle the notification panel.
 *
 * @param props Bell props including unread count and optional open state.
 * @returns The bell trigger element.
 *
 * @example
 * ```tsx
 * <NotificationBell unreadCount={3} onClick={() => setOpen(true)} isOpen={open} />
 * ```
 */
export function NotificationBell({
  unreadCount = 0,
  onClick,
  isOpen = false,
  ariaControls,
  className,
}: NotificationBellProps): JSX.Element {
  const { plural, t } = useNotificationCopy()
  const accessibleCount =
    unreadCount > 99
      ? t('notifications.bell.countOverflowAria')
      : plural('notifications.bell.countAria', unreadCount)

  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={unreadCount > 0 ? accessibleCount : t('notifications.bell.openAria')}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={ariaControls}
      whileHover={{ scale: 1.05, y: -1 }}
      whileTap={{ scale: 0.96 }}
      className={cn(
        'relative rounded-full p-2 text-muted-foreground transition-colors hover:bg-[var(--gn-notifications-panel-surface-muted)] hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        className,
      )}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {unreadCount > 0 ? (
        <span
          aria-hidden="true"
          className="absolute right-0 top-0 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full border border-border bg-destructive px-1 text-[10px] font-bold text-white shadow-[0_0_8px_var(--gn-notifications-badge-glow)]"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      ) : null}
    </motion.button>
  )
}
